/* eslint-disable react/jsx-no-bind */
// @ts-nocheck
import React, { Component } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import {
  assoc,
  dissoc,
  filter,
  isEmpty,
  map,
  pathOr,
  pipe,
  propEq,
  reject,
  view,
  mergeDeepLeft,
} from 'ramda';

import Select from '../../forms/Select';
import Option from '../../forms/Option';

import SearchOptions from '../SearchOptions';
import FilterList, { FilterItem, addSeparators } from '../FilterList';
import {
  getGroupFiltersLens,
  setBroadcastChannel,
  deselectFilter,
  selectFilter,
} from '../models/mediaSearchOptions';

import styles from './BasicSearchFilters.css';
import { FilterCheckbox } from '../FilterList/FilterList';

/**
 * Drops empty array children
 * @type {<T>(xxs: T[][]) => T[][]}
 */
export const excludeEmpty = reject(isEmpty);

// getScopeOptions :: Boolean -> SearchOptions -> []
const getScopeOptions = (showScopeOptions, searchOptions) =>
  showScopeOptions
    ? view(getGroupFiltersLens('scope'), searchOptions)
    : [];

// showContentOptions :: Boolean -> SearchOptions -> []
const getContentOptions = (showContentOptions, searchOptions) =>
  showContentOptions
    ? view(getGroupFiltersLens('content'), searchOptions)
    : [];

// getMyMediaOptions :: Boolean -> SearchOptions -> []
const getMyMediaOptions = (showMyMediaOptions, searchOptions) =>
  showMyMediaOptions
    ? view(getGroupFiltersLens('myMedia'), searchOptions)
    : [];

class FilterItemByType extends Component {
  static propTypes = {
    name: string,
    type: string,
    text: string,
    onSelect: func.isRequired,
    onDeselect: func.isRequired,
    selected: bool,
    showMyPropertiesDropdown: bool,
  };

  onFilterClick = () => this.props.onSelect(this.props.type, this.props.name);

  onCheckboxChange = checked =>
    (checked ? this.props.onSelect : this.props.onDeselect)(
      this.props.type,
      this.props.name
    );

  render() {
    const { selected, text, type } = this.props;
    return type === 'checkbox' ? (
      <FilterCheckbox isOn={selected} onChange={this.onCheckboxChange}>{text}</FilterCheckbox>
    ) : (
      <FilterItem isOn={selected} onClick={this.onFilterClick}>
        {text}
      </FilterItem>
    );
  }
}

function PropertiesDropdown(props) {
  const {
    selected,
    value,
    onSelect,
    availableProperties = [],
  } = props;

  const availablePropertiesNames = availableProperties?.map(property => {
    if (/_[a-z0-9]{8}(?:-[a-z0-9]{4}){3}-[a-z0-9]{12}$/.test(property)) {
      return property.split('_')?.[0];
    } else {
      return property;
    }
  }) || []

  const defaultOption = {
    name: `${availableProperties.length} properties`,
    value: availablePropertiesNames,
  };

  const propertiesList = [defaultOption].concat(
    availableProperties
      .map(property => {
        if (/_[a-z0-9]{8}(?:-[a-z0-9]{4}){3}-[a-z0-9]{12}$/.test(property)) {
          const [name, uuid = name] = property.split('_');
          return { name, value: name, uuid };
        } else {
          return { name: property, value: property, uuid: 'no-uuid' };
        }
      })
      .sort((a, b) => {
        return a.name.localeCompare(b.name, 'en', { numeric: true, sensitivity: 'base' });
      })
  );

  return (
    <div className={styles.PropertiesDropdown}>
      <Select
        onChange={({ target }) => {
          onSelect(
            availablePropertiesNames.join(',') === target.value
              ? availablePropertiesNames
              : [target.value]
          );
        }}
        disabled={!selected}
        value={(value || []).join(',')}
      >
        {(propertiesList.map(
          ({ name, value: _value, uuid }) => (
            <Option
              key={`option_${uuid}`}
              label={name}
              value={_value}
            />
          )
        ))}
      </Select>
    </div>
  );
}

PropertiesDropdown.propTypes = {
  name: string,
  type: string,
  text: string,
  onSelect: func.isRequired,
  onDeselect: func,
  selected: bool,
  value: arrayOf(string),
  availableProperties: arrayOf(string),
};

const QUERY_GROUPS = [
  'packageType',
  'field',
  'constraint',
  'status',
  'generic',
  'checkbox',
];

const addTypeToSearchOptions = searchOptions => groupName =>
  map(
    assoc('type', groupName),
    pathOr([], [groupName, 'filters'], searchOptions)
  );

const getBasicFilterGroups = searchOptions => {
  return pipe(
    map(
      pipe(
        addTypeToSearchOptions(searchOptions),
        filter(propEq('basicFilter', true))
      )
    ),
    reject(isEmpty)
  )(QUERY_GROUPS);
};

class BasicSearchFilters extends Component {
  static propTypes = {
    searchOptions: shape(dissoc('searchText', SearchOptions)).isRequired,
    showScopeOptions: bool, // default false
    showMyMediaOptions: bool, // default false
    showMyPropertiesDropdown: bool,
    showMyContentFilter: bool,
    onOptionsChange: func.isRequired,
    availableProperties: arrayOf(string),
  };

  selectFilter = (filterType, name) => {
    this.props.onOptionsChange(
      selectFilter(filterType, name, this.props.searchOptions)
    );
  };

  selectProperty = (value) => {
    const newOpts = selectFilter('scope', 'my', this.props.searchOptions);
    newOpts.scope.filters.find(el => el.name === 'my').value = value;
    this.props.onOptionsChange(newOpts);
  }

  deselectFilter = (filterType, name) => {
    this.props.onOptionsChange(
      deselectFilter(filterType, name, this.props.searchOptions)
    );
  };

  onBroadcastChange = ({ target }) => {
    setBroadcastChannel(target.value, this.props.searchOptions).map(newOpts =>
      this.props.onOptionsChange(newOpts)
    );
  };

  renderFilterItems() {
    const {
      showScopeOptions,
      searchOptions,
      showMyPropertiesDropdown,
      showMyContentFilter,
      showMyMediaOptions,
      availableProperties = [],
    } = this.props;

    const scopeOptions = getScopeOptions(showScopeOptions, searchOptions) || [];
    const myMediaOptions = getMyMediaOptions(showMyMediaOptions, searchOptions) || [];
    const scopeFilters = [
      ...map(
        pipe(
          mergeDeepLeft({
            type: 'scope',
            onSelect: this.selectFilter,
            onDeselect: this.deselectFilter,
          }),
          filterProps => {
            const slugifiedValue = filterProps.text.replace(/([A-Z]+)/g, '_$1')
              .replace(/\s+/g, '_')
              .replace(/_+/g, '_')
              .replace(/^_|_$/g, '');
            const key = `filter_item_by_type_${filterProps.type}_${slugifiedValue}`;
            return <FilterItemByType key={key} {...filterProps} />;
          }
        ),
        scopeOptions
      ),
      ...(
        showMyPropertiesDropdown && availableProperties.length > 1
          ? [
            <PropertiesDropdown
              {...scopeOptions[1]}
              availableProperties={availableProperties}
              type="scope"
              onSelect={this.selectProperty}
              key={'properties_dropdown'}
            />,
          ]
          : []
      ),
    ];

    const myMediaFilters = map(
      pipe(
        mergeDeepLeft({ type: 'myMedia', onSelect: this.selectFilter, onDeselect: this.deselectFilter }),
        filterProps => <FilterItemByType key={`type_${filterProps.type}`} {...filterProps} />
      ),
      myMediaOptions
    );

    const basicFilterGroups = getBasicFilterGroups(searchOptions).map(
      map(bf => (
        <FilterItemByType
          key={bf.type + '_' + bf.name}
          {...bf}
          onSelect={this.selectFilter}
          onDeselect={this.deselectFilter}
        />
      ))
    );
    const hasClaimedBio = searchOptions?.content?.filters?.find(el => el?.name === 'my')?.value?.length > 0;
    const contentOptions = getContentOptions(showMyContentFilter && hasClaimedBio, searchOptions) || [];
    const contentFilters = map(
      pipe(
        mergeDeepLeft({ type: 'content', onSelect: this.selectFilter, onDeselect: this.deselectFilter }),
        filterProps => <FilterItemByType key={`type_${filterProps.type}`} {...filterProps} />
      ),
      contentOptions
    );
    return addSeparators(excludeEmpty([scopeFilters, ...basicFilterGroups, contentFilters, myMediaFilters]));
  }

  renderBroadcastDropdown() {
    const { filters } = this.props.searchOptions.packageType;
    const broadcastFilter = filters.find(f => f.name === 'broadcast');

    const channels = broadcastFilter && broadcastFilter.channels;

    if (
      !Array.isArray(channels) ||
      channels.length <= 1 ||
      !broadcastFilter.selected
    ) {
      return null;
    }

    return (
      <Select
        className={styles.broadcastMenu}
        onChange={this.onBroadcastChange}
        defaultValue={broadcastFilter.channel}
      >
        {channels.map(({ value, name }) => (
          <Option
            key={value.uuid}
            label={name}
            value={value}
          />
        ))}
      </Select>
    );
  }

  render() {
    const { packageType } = this.props.searchOptions;
    return (
      <div className={styles.BasicSearchFilter}>
        <FilterList label="Filter">{this.renderFilterItems()}</FilterList>
        {packageType && this.renderBroadcastDropdown()}
      </div>
    );
  }
}

export default BasicSearchFilters;
